import type { ReactNode } from 'react';
import { ToastIcon } from './toast-icons';

export interface CustomToastProps {
  title: string;
  description?: string;
  type: 'success' | 'error' | 'info' | 'warning';
  action?: ReactNode;
}

const TOAST_STYLES = {
  success: {
    container: 'bg-green-50 border-green-200 text-green-900',
    title: 'text-green-900 font-semibold',
    description: 'text-green-700',
  },
  error: {
    container: 'bg-red-50 border-red-200 text-red-900',
    title: 'text-red-900 font-semibold',
    description: 'text-red-700',
  },
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-900',
    title: 'text-blue-900 font-semibold',
    description: 'text-blue-700',
  },
  warning: {
    container: 'bg-orange-50 border-orange-200 text-orange-900',
    title: 'text-orange-900 font-semibold',
    description: 'text-orange-700',
  },
};

export function CustomToast({ title, description, type, action }: CustomToastProps) {
  const styles = TOAST_STYLES[type];

  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${styles.container} shadow-lg`}>
      <ToastIcon type={type} size={24} className="flex-shrink-0 mt-0.5" />

      <div className="flex-1 min-w-0">
        <div className={`text-sm ${styles.title}`}>{title}</div>

        {description && <div className={`text-sm mt-1 ${styles.description}`}>{description}</div>}

        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

// Helper functions to create custom toasts
export const createSuccessToast = (title: string, description?: string, action?: ReactNode) => (
  <CustomToast type="success" title={title} description={description} action={action}/>
);

export const createErrorToast = (title: string, description?: string, action?: ReactNode) => (
  <CustomToast type="error" title={title} description={description} action={action} />
);

export const createInfoToast = (title: string, description?: string, action?: ReactNode) => (
  <CustomToast type="info" title={title} description={description} action={action} />
);

export const createWarningToast = (title: string, description?: string, action?: ReactNode) => (
  <CustomToast type="warning" title={title} description={description} action={action} />
);
